// 원서 워크북(지인에듀 mode=book) 한 세트를 학생 화면의 활동 목록으로 바꾼다.
//
// 지인에듀 안내(2026-10-16 받은 README): 종이 워크북은 PART 순서대로 나가고, PART 안에서는 기본 활동이
// 먼저, 그 PART 의 그 밖의 유형(extra)이 extra_types 순서대로 뒤에 나간다. 문항은 늘 목록의 앞에서부터
// counts 만큼만 쓴다(그래야 종이와 같다). counts 가 0 인 활동은 내지 않는다.
//
// 활동마다 학생이 답하는 모양(kind)은 아래 몇 가지뿐이다. 화면(BookWorkbookPage)은 kind 만 보고 그린다.
//   choice   보기 고르기 (binary 의 참/거짓, Fact/Opinion 도 보기 고르기로 본다)
//   input    한 줄 입력. answer 가 있으면 자동 채점, gradeable=false 면 모범 답만 보여 준다
//   written  여러 줄 쓰기. 채점하지 않고 모범 답을 보여 준다
//   match    선 긋기 (문항 하나에 짝 여러 개)
//   order    순서 맞추기 (문항 하나)
//   multi    여러 개 고르기 (아카데믹 요약, 문항 하나)
//   classify 범주 나누기 (줄마다 범주 하나)
//   gap      빈칸 채우기 + 단어 상자 (생각하기 요약)
//   table    표 채우기 (채점 없음)
//   crossword, word_search

const PART_LIST = ["vocabulary", "grammar", "comprehension", "thinking", "academic", "writing"];

export const PART_LABEL = {
  vocabulary: "Vocabulary",
  grammar: "Grammar & Sentences",
  comprehension: "Comprehension",
  thinking: "Thinking",
  academic: "Academic Reading",
  writing: "Writing",
};

const BASE_ACTIVITY_LIST_BY_PART = {
  vocabulary: ["trace", "meaning", "match_picture", "match_word", "context", "crossword", "word_search", "scramble"],
  grammar: [],
  comprehension: ["true_false", "multiple_choice", "order", "main_idea"],
  thinking: ["inference", "cause_effect", "traits", "summary"],
  academic: [
    "academic_vocabulary",
    "academic_reference",
    "academic_factual",
    "academic_negative",
    "academic_inference",
    "academic_purpose",
    "academic_simplification",
    "academic_insertion",
    "academic_summary",
    "academic_table",
  ],
  writing: ["writing"],
};

// 지시문이 안 왔을 때 쓰는 이름. 지인에듀는 instructions 에 선생님이 고른 언어로 지시문을 준다.
const BASE_ACTIVITY_LABEL = {
  trace: "단어 따라 쓰기",
  meaning: "단어 뜻 쓰기",
  match_picture: "그림 보고 선 긋기",
  match_word: "단어 선 긋기",
  context: "문맥 속 뜻 고르기",
  crossword: "크로스워드",
  word_search: "워드서치",
  scramble: "철자 섞기",
  true_false: "맞는지 틀린지 고르기",
  multiple_choice: "내용 이해 고르기",
  order: "사건 순서",
  main_idea: "주제와 제목",
  inference: "추론하기",
  cause_effect: "원인과 결과",
  traits: "인물의 성격",
  summary: "요약 빈칸",
  academic_vocabulary: "어휘",
  academic_reference: "지칭",
  academic_factual: "세부 내용",
  academic_negative: "내용 불일치",
  academic_inference: "추론",
  academic_purpose: "글쓴이의 의도",
  academic_simplification: "문장 단순화",
  academic_insertion: "문장 넣기",
  academic_summary: "요약 고르기",
  academic_table: "표 완성",
  writing: "쓰기",
};

const takeCount = (list, count) => (Array.isArray(list) ? list.slice(0, Math.max(0, count || 0)) : []);

const normalizeText = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^a-z0-9'\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const isSameAnswer = (input, answer) =>
  normalizeText(input) !== "" && normalizeText(input) === normalizeText(answer);

// 같은 학생은 언제 열어도 같은 순서로 보이게 섞는다(이어 하기·다시 보기에서 순서가 바뀌면 안 된다).
const seedOf = (text) => {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export const seededShuffle = (list, seedText) => {
  const result = [...list];
  let seed = seedOf(seedText) || 1;
  const random = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return ((seed >>> 0) % 100000) / 100000;
  };
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  // 섞었는데 그대로면 한 칸 민다. 순서 맞추기가 처음부터 정답이면 문제가 아니다.
  if (result.length > 1 && result.every((item, index) => item === list[index])) {
    result.push(result.shift());
  }
  return result;
};

const choiceItem = (source, questionKey = "question") => ({
  quote: source.quote || "",
  prompt: source[questionKey] || source.prompt || "",
  options: source.options || [],
  answerIndex: typeof source.answer_index === "number" ? source.answer_index : -1,
  explanation: source.explanation_ko || "",
  evidence: source.evidence || "",
});

const buildBaseActivity = (key, chapter, seedText) => {
  const counts = chapter.counts || {};
  const count = counts[key] || 0;
  if (count <= 0) return null;

  const vocabulary = chapter.vocabulary || {};
  const comprehension = chapter.comprehension || {};
  const thinking = chapter.thinking || {};
  const academic = chapter.academic || {};
  const wordList = vocabulary.words || [];
  const isBeginner = chapter.level === "beginner";

  switch (key) {
    case "trace":
      return {
        kind: "input",
        items: takeCount(wordList, count).map((word) => ({
          prompt: word.meaning_ko || "",
          hint: word.word,
          answer: word.word,
          gradeable: true,
        })),
      };
    case "meaning":
      return {
        kind: "input",
        items: takeCount(wordList, count).map((word) => ({
          prompt: word.word,
          subPrompt: [word.part_of_speech, word.definition].filter(Boolean).join(" · "),
          answer: word.meaning_ko || "",
          evidence: word.sentence || "",
          gradeable: false,
          placeholder: "우리말 뜻",
        })),
      };
    case "match_picture": {
      const pictureList = takeCount(chapter.pictures, count).filter((picture) => picture.url);
      if (pictureList.length < 2) return null;
      return {
        kind: "match",
        items: [
          {
            pairs: pictureList.map((picture) => ({ leftImage: picture.url, left: picture.word, right: picture.word })),
          },
        ],
      };
    }
    case "match_word":
      return {
        kind: "match",
        items: [
          {
            pairs: takeCount(wordList, count).map((word) => ({
              left: word.word,
              right: isBeginner ? word.meaning_ko : word.definition || word.meaning_ko,
            })),
          },
        ],
      };
    case "context":
      return {
        kind: "choice",
        items: takeCount(vocabulary.context_clues, count).map((clue) => ({
          ...choiceItem(clue),
          quote: clue.sentence || "",
          prompt: `"${clue.word}" 의 뜻으로 알맞은 것은?`,
        })),
      };
    case "crossword": {
      const crossword = (chapter.puzzles || {}).crossword;
      if (!crossword || !Array.isArray(crossword.cells) || crossword.cells.length === 0) return null;
      return { kind: "crossword", crossword };
    }
    case "word_search": {
      const wordSearch = (chapter.puzzles || {}).word_search;
      if (!wordSearch || !Array.isArray(wordSearch.rows) || wordSearch.rows.length === 0) return null;
      return { kind: "word_search", wordSearch };
    }
    case "scramble":
      return {
        kind: "input",
        items: takeCount(
          wordList.filter((word) => String(word.word || "").length <= 9),
          count
        ).map((word, index) => ({
          prompt: seededShuffle(String(word.word).toUpperCase().split(""), `${seedText}:scramble:${index}`).join(" "),
          subPrompt: word.meaning_ko || "",
          answer: word.word,
          gradeable: true,
          isLetterPuzzle: true,
        })),
      };
    case "true_false": {
      const statementList = takeCount(comprehension.true_false, count);
      const hasNotGiven = statementList.some((statement) => statement.answer === "NG");
      const labelList = hasNotGiven ? ["True", "False", "Not Given"] : ["True", "False"];
      const answerCodeList = hasNotGiven ? ["T", "F", "NG"] : ["T", "F"];
      return {
        kind: "choice",
        isInline: true,
        items: statementList.map((statement) => ({
          prompt: statement.statement,
          options: labelList,
          answerIndex: answerCodeList.indexOf(statement.answer),
          explanation: statement.explanation_ko || "",
          evidence: statement.evidence || "",
        })),
      };
    }
    case "multiple_choice":
      return { kind: "choice", items: takeCount(comprehension.multiple_choice, count).map((one) => choiceItem(one)) };
    case "order": {
      const eventList = comprehension.events || [];
      if (eventList.length < 2) return null;
      return { kind: "order", items: [{ correctList: eventList }] };
    }
    case "main_idea":
      return {
        kind: "choice",
        items: [comprehension.main_idea, comprehension.best_title]
          .filter((one) => one && Array.isArray(one.options))
          .map((one) => choiceItem(one)),
      };
    case "inference":
      return { kind: "choice", items: takeCount(thinking.inference, count).map((one) => choiceItem(one)) };
    case "cause_effect":
      // 종이는 번갈아 비운다. 0·2·4번째는 원인을 주고 결과를, 1·3번째는 결과를 주고 원인을 묻는다.
      return {
        kind: "written",
        items: takeCount(thinking.cause_effect, count).map((pair, index) =>
          index % 2 === 0
            ? { quote: `Cause: ${pair.cause}`, prompt: "What happened as a result?", answer: pair.effect }
            : { quote: `Effect: ${pair.effect}`, prompt: "What caused this?", answer: pair.cause }
        ),
      };
    case "traits":
      return {
        kind: "written",
        items: takeCount(thinking.traits, count).map((trait) => ({
          prompt: `${trait.character} is ${trait.trait}. Find evidence from the chapter.`,
          answer: trait.evidence || "",
        })),
      };
    case "summary": {
      const summary = thinking.summary;
      if (!summary || !summary.text) return null;
      return {
        kind: "gap",
        items: [
          {
            text: summary.text,
            answerList: summary.answers || [],
            bankList: seededShuffle([...(summary.answers || []), ...(summary.distractors || [])], `${seedText}:summary`),
          },
        ],
      };
    }
    case "academic_vocabulary":
    case "academic_reference":
    case "academic_factual":
    case "academic_negative":
    case "academic_inference":
    case "academic_purpose":
    case "academic_simplification": {
      const sourceKey = key.replace("academic_", "");
      return {
        kind: "choice",
        usesPassage: true,
        items: takeCount(academic[sourceKey], count).map((one) => choiceItem(one)),
      };
    }
    case "academic_insertion":
      return {
        kind: "choice",
        usesPassage: true,
        items: takeCount(academic.insertion, count)
          .filter((one) => Array.isArray(one.sentences) && one.target_index >= 1)
          .map((one) => {
            const targetSentence = one.sentences[one.target_index];
            const restList = one.sentences.filter((_, index) => index !== one.target_index);
            const markerList = ["A", "B", "C", "D"];
            return {
              prompt: `넣을 문장: ${targetSentence}`,
              quote: restList.map((sentence, index) => `${sentence} [■${markerList[index]}]`).join(" "),
              options: markerList.slice(0, restList.length).map((marker) => `[■${marker}]`),
              answerIndex: one.target_index - 1,
              explanation: one.explanation_ko || "",
            };
          }),
      };
    case "academic_summary": {
      const summary = academic.summary;
      if (!summary || !Array.isArray(summary.options)) return null;
      return {
        kind: "multi",
        usesPassage: true,
        items: [
          {
            prompt: summary.introduction || "",
            options: summary.options,
            answerIndexList: summary.answer_indices || summary.answer_index_list || [],
            explanation: summary.explanation_ko || "",
          },
        ],
      };
    }
    case "academic_table": {
      const table = academic.table;
      if (!table || !Array.isArray(table.rows)) return null;
      return {
        kind: "classify",
        usesPassage: true,
        items: [
          {
            categoryList: [...(table.categories || []), "해당 없음"],
            rowList: table.rows.map((row) => ({
              text: row.statement,
              answerIndex: row.category_index >= 0 ? row.category_index : (table.categories || []).length,
            })),
          },
        ],
      };
    }
    case "writing": {
      const writing = chapter.writing || {};
      const prompt = chapter.language === "en" ? writing.prompt : writing.prompt_ko || writing.prompt;
      if (!prompt) return null;
      return {
        kind: "written",
        isEssay: true,
        items: [
          {
            prompt,
            helpList: writing.organizer || [],
            rubricList: writing.rubric || [],
            answer: writing.model_answer || "",
          },
        ],
      };
    }
    default:
      return null;
  }
};

const missingLetterPrompt = (word) =>
  word
    .split("")
    .map((letter, index) => (index === 0 || index % 2 === 1 ? letter : "_"))
    .join(" ");

const buildExtraActivity = (key, typeInfo, chapter, seedText) => {
  const count = (chapter.counts || {})[key] || 0;
  if (count <= 0) return null;

  if (typeInfo.mode === "missing") {
    return {
      kind: "input",
      items: takeCount(
        ((chapter.vocabulary || {}).words || []).filter((word) => String(word.word || "").length >= 4),
        count
      ).map((word) => ({
        prompt: missingLetterPrompt(String(word.word)),
        subPrompt: word.meaning_ko || "",
        answer: word.word,
        gradeable: true,
        isLetterPuzzle: true,
      })),
    };
  }

  const itemList = takeCount((chapter.extra || {})[key], count);
  if (itemList.length === 0) return null;

  switch (typeInfo.mode) {
    case "choice":
      return { kind: "choice", items: itemList.map((one) => choiceItem(one, "prompt")) };
    case "binary": {
      const labelList = typeInfo.labels || ["Yes", "No"];
      return {
        kind: "choice",
        isInline: true,
        items: itemList.map((one) => ({
          quote: one.quote || "",
          prompt: one.prompt,
          options: labelList,
          answerIndex: labelList.findIndex((label) => normalizeText(label) === normalizeText(one.answer)),
          explanation: one.explanation_ko || "",
          evidence: one.evidence || "",
        })),
      };
    }
    case "short":
      return {
        kind: "input",
        items: itemList.map((one) => ({
          quote: one.quote || "",
          prompt: one.prompt,
          answer: one.answer,
          explanation: one.explanation_ko || "",
          gradeable: false,
        })),
      };
    case "gap":
      return {
        kind: "input",
        bankList: typeInfo.bank ? seededShuffle(itemList.map((one) => one.answer), `${seedText}:${key}`) : null,
        items: itemList.map((one) => ({
          quote: one.quote || "",
          prompt: one.prompt,
          answer: one.answer,
          explanation: one.explanation_ko || "",
          gradeable: true,
        })),
      };
    case "written":
      return {
        kind: "written",
        items: itemList.map((one) => ({
          quote: one.quote || "",
          prompt: one.prompt,
          helpList: one.options || [],
          answer: one.answer,
          explanation: one.explanation_ko || "",
        })),
      };
    case "unscramble":
      return {
        kind: "order",
        isSentence: true,
        items: itemList
          .filter((one) => Array.isArray(one.options) && one.options.length > 1)
          .map((one) => ({ prompt: one.prompt, correctList: one.options, explanation: one.explanation_ko || "" })),
      };
    case "order":
      return {
        kind: "order",
        items: itemList
          .filter((one) => Array.isArray(one.options) && one.options.length > 1)
          .map((one) => ({ prompt: one.prompt, correctList: one.options, explanation: one.explanation_ko || "" })),
      };
    case "match":
      return {
        kind: "match",
        items: itemList
          .filter((one) => Array.isArray(one.pairs) && one.pairs.length > 1)
          .map((one) => ({ prompt: one.prompt, pairs: one.pairs.map((pair) => ({ left: pair.left, right: pair.right })) })),
      };
    case "classify":
      return {
        kind: "classify",
        items: itemList
          .filter((one) => Array.isArray(one.options) && Array.isArray(one.pairs))
          .map((one) => ({
            prompt: one.prompt,
            categoryList: one.options,
            rowList: one.pairs.map((pair) => ({ text: pair.left, answerIndex: one.options.indexOf(pair.right) })),
          })),
      };
    case "table":
      return {
        kind: "table",
        items: itemList.map((one) => ({
          prompt: one.prompt,
          headerList: typeInfo.headers || one.options || ["Answer"],
          rowList: (one.pairs || []).map((pair) => ({
            label: pair.left,
            answerList: String(pair.right || "").split("|").map((cell) => cell.trim()),
          })),
        })),
      };
    default:
      return null;
  }
};

// 원서 한 세트 → 화면에 낼 활동 목록. 문항이 하나도 없는 활동은 뺀다.
export const buildBookActivityList = (book, seedText) => {
  if (!book || typeof book !== "object") return [];
  const instructions = book.instructions || {};
  const extraTypes = book.extra_types || {};
  const activityList = [];

  PART_LIST.forEach((part) => {
    const keyList = [
      ...BASE_ACTIVITY_LIST_BY_PART[part].map((key) => ({ key, isExtra: false })),
      ...Object.keys(extraTypes)
        .filter((key) => extraTypes[key].part === part)
        .map((key) => ({ key, isExtra: true })),
    ];

    keyList.forEach(({ key, isExtra }) => {
      const activity = isExtra
        ? buildExtraActivity(key, extraTypes[key], book, `${seedText}:${key}`)
        : buildBaseActivity(key, book, `${seedText}:${key}`);
      if (!activity) return;
      const hasContent =
        activity.kind === "crossword" || activity.kind === "word_search" || (activity.items || []).length > 0;
      if (!hasContent) return;

      const label = isExtra ? extraTypes[key].label || key : BASE_ACTIVITY_LABEL[key] || key;
      activityList.push({
        ...activity,
        key,
        part,
        label,
        instruction: instructions[key] || "",
        passage: activity.usesPassage ? (book.academic || {}).passage || [] : null,
      });
    });
  });

  return activityList;
};
